/**
 * `#uc2UnitsChart` — the Overview's per-unit bars, which follow the active chip (2 Oct
 * chart round, design §2.2). One picture of the whole programme beside the map, so the
 * chip a reader presses is also a ranking they can read:
 *
 * * pattern → cropland change per unit, coloured by its class (signed, zero baseline);
 * * cropland → the same numbers, one neutral colour, the step left to the map;
 * * rainfall → the rainfall change over the period, signed;
 * * practices → adoption of the selected group, against the programme mean;
 * * greening → the vegetation trend per year;
 * * land cover → each unit's cropland gain and loss as a pair of bars.
 *
 * At country level the pattern chip reads the run's own `share_positive_pct`, and the two
 * raster chips (client round 3 Oct 2026) read each country's **mean of its watersheds'**
 * numbers — the four land-cover change shares, and the vegetation trend — derived here
 * from the watershed rows the pane already holds, exactly as the map derives the three
 * evidence chips' country shares. The remaining chips leave their derived share to the
 * map rather than restating it as a ranking.
 *
 * Everything is read off the units rows the choropleth is already painted from, plus the
 * one detail a click has already fetched — no request of its own.
 */
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { ChartFrame } from '../../components/charts/ChartFrame';
import { axisTitle } from '../../components/charts/scale';
import type { IntelUnitRow } from '../../lib/api';
import { DimensionBars, type DimRow } from './charts/DimensionBars';
import { PairedBars, type PairedBarsRow } from './charts/PairedBars';
import { croplandStep, meanOf, watershedsOf } from './uc2Paint';
import {
  CROPLAND_BASE,
  UC2_LC_CHANGE_COLORS,
  POSITIVE_BASE,
  PRACTICE_BASE,
  RAINFALL_BASE,
  familySteps,
  normalisePractice,
  type Uc2Chip,
} from './uc2Constants';

export interface Uc2UnitsChartProps {
  rows: IntelUnitRow[];
  chip: Uc2Chip;
  /** `#intelLevel` — `'watershed'` or `'admin0'`. */
  level: string;
  practice: string;
  /** `summary.programme_means[practice]` — the practices chart's reference tick. */
  programmeMean?: number | null;
  /** The run's own stability band, in percent, for the pattern chip's colouring. */
  band?: number;
  /**
   * The watershed rows, whatever the level switch says — the country-level land-cover
   * and greening bars are means over these. The pane holds them already (the same cached
   * `uc2/units?level=watershed` it fetched on open), so this costs no request.
   */
  watershedRows?: IntelUnitRow[];
  /** The focused unit: kept in the chart with every other, but bold and outlined. */
  selectedId?: string | null;
}

/** Units in a stable reading order: name, so the same place is in the same place. */
function byName(rows: IntelUnitRow[]): IntelUnitRow[] {
  return [...rows].sort((a, b) => a.name.localeCompare(b.name));
}

export function Uc2UnitsChart({
  rows,
  chip,
  level,
  practice,
  programmeMean,
  band,
  watershedRows,
  selectedId = null,
}: Uc2UnitsChartProps) {
  const { t } = useTranslation();
  if (!rows.length) return null;

  const units = byName(rows);
  const group = normalisePractice(practice);
  const isCountry = level === 'admin0';
  const rowTitle = t(isCountry ? 'charts.axis.country' : 'charts.axis.watershed');
  const croplandColors = familySteps(CROPLAND_BASE);
  const rainColor = familySteps(RAINFALL_BASE)[2];
  const practiceColor = familySteps(PRACTICE_BASE)[2];
  const positiveColor = familySteps(POSITIVE_BASE)[2];

  const frame = (title: string, body: ReactNode, testId?: string) => (
    <div
      id="uc2UnitsChart"
      className="intel-unitschart intel-card"
      {...(testId ? { 'data-testid': testId } : {})}
    >
      <h6 className="intel-card-title">{title}</h6>
      {body}
    </div>
  );

  const note = (key: string, title: string, testId: string) =>
    frame(
      title,
      <p className="note" data-testid={testId}>
        {t(key)}
      </p>,
    );

  const bars = (
    title: string,
    rowsOut: DimRow[],
    options: { max?: number; xLabel?: string } = {},
  ) => {
    if (!rowsOut.some((r) => r.value != null)) {
      return note('intel.uc2.chart.unitsPending', title, 'uc2-units-pending');
    }
    // #749: each watershed's class word ("Positive change") in its hover popup.
    const withStatus = isCountry
      ? rowsOut
      : rowsOut.map((r) => {
          const category = units.find((u) => u.unit_id === r.id)?.category;
          return category && !r.status
            ? { ...r, status: t(`intel.uc2.category.${category}`, { defaultValue: category }) }
            : r;
        });
    return frame(
      title,
      <ChartFrame
        title={title}
        xLabel={options.xLabel}
        yLabel={rowTitle}
        testId="uc2-units-chart"
      >
        {() => (
          <DimensionBars
            rows={withStatus}
            {...(options.max != null ? { max: options.max } : {})}
            selectedId={selectedId}
            ariaLabel={title}
            referenceLabel={t('intel.chart.reference')}
            refCaption={t('intel.chart.ref')}
          />
        )}
      </ChartFrame>,
      'uc2-units-bars',
    );
  };

  /**
   * One land-cover change class for one chart row: the unit's own share at watershed
   * level, and at country level the mean of that country's watersheds' shares — the
   * client's own reading of "what did this country's land do", derived here from the
   * watershed rows rather than asked of the server, exactly as the map derives the
   * country shares for the three evidence chips.
   */
  const lcOf = (row: IntelUnitRow, key: 'gain' | 'loss' | 'veg_gain' | 'veg_loss') => {
    if (!isCountry) {
      const own = row.lc_change?.[key];
      return typeof own === 'number' ? own : null;
    }
    return meanOf(watershedsOf(watershedRows, row.unit_id).map((w) => w.lc_change?.[key] ?? null));
  };

  /** The vegetation trend per year, the same way round. */
  const ndviOf = (row: IntelUnitRow) => {
    if (!isCountry) return typeof row.ndvi_trend === 'number' ? row.ndvi_trend : null;
    return meanOf(watershedsOf(watershedRows, row.unit_id).map((w) => w.ndvi_trend ?? null));
  };

  if (chip === 'landcover') {
    // All four change classes, as two paired charts rather than four bars in one: gain
    // against loss is the comparison a reader makes, and cropland against vegetation is
    // the other one. Four bars per unit in one frame is a wall at the dock's width. Each
    // pair carries the raster's own two colours, so a loss is never drawn in the green
    // the map paints a gain with.
    const pairs = (
      first: 'gain' | 'veg_gain',
      second: 'loss' | 'veg_loss',
    ): PairedBarsRow[] =>
      units.map((row) => ({
        id: row.unit_id,
        label: row.name,
        a: lcOf(row, first),
        b: lcOf(row, second),
      }));
    const crop = pairs('gain', 'loss');
    const veg = pairs('veg_gain', 'veg_loss');
    if (![...crop, ...veg].some((p) => p.a != null || p.b != null)) {
      return note(
        'intel.uc2.chart.unitsPending',
        t('intel.uc2.chart.unitsLandcover'),
        'uc2-units-pending',
      );
    }
    return frame(
      t('intel.uc2.chart.unitsLandcover'),
      <>
        <ChartFrame
          title={t('intel.uc2.chart.unitsLandcover')}
          xLabel={t('insights.axis.areaShare')}
          yLabel={rowTitle}
          testId="uc2-units-crop-change"
        >
          {() => (
            <PairedBars
              rows={crop}
              labels={[t('intel.uc2.lcChangeClass.crop_gain'), t('intel.uc2.lcChangeClass.crop_loss')]}
              unit="%"
              colors={[UC2_LC_CHANGE_COLORS.crop_gain, UC2_LC_CHANGE_COLORS.crop_loss]}
              selectedId={selectedId}
              ariaLabel={t('intel.uc2.chart.unitsLandcover')}
            />
          )}
        </ChartFrame>
        <h6 className="intel-block-title">{t('intel.uc2.chart.unitsVegetation')}</h6>
        <ChartFrame
          title={t('intel.uc2.chart.unitsVegetation')}
          xLabel={t('insights.axis.areaShare')}
          yLabel={rowTitle}
          testId="uc2-units-veg-change"
        >
          {() => (
            <PairedBars
              rows={veg}
              labels={[t('intel.uc2.lcChangeClass.veg_gain'), t('intel.uc2.lcChangeClass.veg_loss')]}
              unit="%"
              colors={[UC2_LC_CHANGE_COLORS.veg_gain, UC2_LC_CHANGE_COLORS.veg_loss]}
              selectedId={selectedId}
              ariaLabel={t('intel.uc2.chart.unitsVegetation')}
            />
          )}
        </ChartFrame>
      </>,
      'uc2-units-paired',
    );
  }

  if (chip === 'greening') {
    return bars(
      t(isCountry ? 'intel.uc2.chart.unitsGreeningCountry' : 'intel.uc2.chart.unitsGreening'),
      units.map((row) => ({
        id: row.unit_id,
        label: row.name,
        value: ndviOf(row),
        unit: t('intel.uc2.chart.perYear'),
        decimals: 4,
        color: croplandColors[2],
      })),
      { xLabel: t('intel.axis.ndviTrend') },
    );
  }

  // Country level: the run's own roll-up is the share with positive change. The three
  // evidence chips are shares the map derives from the watershed rows, and the chart
  // leaves those to the map rather than restating a derived number as a ranking. (The two
  // raster chips have already answered above: since the 3 Oct round they read a country as
  // the mean of its own watersheds.)
  if (isCountry) {
    if (chip !== 'pattern') {
      return note('intel.uc2.watershedOnly', t(`intel.uc2.chip.${chip}`), 'uc2-units-country');
    }
    return bars(
      t('intel.uc2.chart.unitsPositive'),
      units.map((row) => ({
        id: row.unit_id,
        label: row.name,
        value: typeof row.share_positive_pct === 'number' ? row.share_positive_pct : null,
        unit: '%',
        color: positiveColor,
      })),
      { max: 100, xLabel: axisTitle(t('intel.axis.positiveShare'), '%') },
    );
  }

  if (chip === 'practices') {
    return bars(
      t('intel.uc2.chart.unitsPractices', { name: t(`intel.uc2.practice.${group}`) }),
      units.map((row) => {
        const value = row.adoption?.[group];
        return {
          id: row.unit_id,
          label: row.name,
          value: typeof value === 'number' ? value : null,
          unit: '%',
          reference: typeof programmeMean === 'number' ? programmeMean : null,
          color: practiceColor,
        };
      }),
      { max: 100, xLabel: t('intel.axis.adoption') },
    );
  }

  if (chip === 'rainfall') {
    return bars(
      t('intel.uc2.chart.unitsRain'),
      units.map((row) => ({
        id: row.unit_id,
        label: row.name,
        value: typeof row.rain_change_pct === 'number' ? row.rain_change_pct : null,
        unit: '%',
        color: rainColor,
      })),
      { xLabel: axisTitle(t('intel.axis.rainChange'), '%') },
    );
  }

  // `pattern` and `cropland`: the same number, coloured by class on the pattern chip and
  // left in one neutral colour on the cropland chip, where the map already carries the step.
  const coloured = chip === 'pattern';
  return bars(
    t('intel.uc2.chart.unitsCropland'),
    units.map((row) => {
      const value = typeof row.cropland_delta_pct === 'number' ? row.cropland_delta_pct : null;
      const step = croplandStep(value, band);
      return {
        id: row.unit_id,
        label: row.name,
        value,
        unit: '%',
        ...(coloured && step != null ? { color: croplandColors[step] } : {}),
      };
    }),
    { xLabel: axisTitle(t('intel.axis.croplandChange'), '%') },
  );
}

export default Uc2UnitsChart;
